/**
 * ProposalItemsTables - Tabelas do snapshot de itens da proposta
 *
 * Renderiza duas tabelas independentes (Produtos e Serviços Recorrentes) a
 * partir do snapshot congelado em `crm_proposal_items`. É 100% somente-leitura
 * — diferente do `DealItemsSummary` da aba Produtos, que é editável e possui
 * totalizadores próprios (aqui os totais ficam no rodapé fixo do modal).
 *
 * CHANGELOG:
 *   RELEASE: 2026-08-12 v1.1.0 - Ícone de Serviços recorrentes igual ao da aba Produtos (Wrench)
 *   RELEASE: 2026-08-11 v1.0.0 - Criação (refatoração do modal de detalhes da
 *            proposta — direção "Grid + rodapé fixo")
 */

import React from 'react';
import { Package, Wrench } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ProposalSnapshotItem } from './proposalItemsAdapter';

interface ProposalItemsTablesProps {
  /** Itens congelados da proposta */
  items: ProposalSnapshotItem[];
  /** Moeda dos valores */
  currency?: string;
}

/** Converte com segurança valores numeric do PG (podem vir como string) */
const num = (value: unknown): number => {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

/** Formata valores monetários */
const money = (value: unknown, currency = 'BRL'): string =>
  num(value).toLocaleString('pt-BR', { style: 'currency', currency });

/** Rótulos de recorrência em pt-BR */
const RECURRENCE_LABELS: Record<string, string> = {
  monthly: 'Mensal',
  quarterly: 'Trimestral',
  semiannual: 'Semestral',
  yearly: 'Anual',
  weekly: 'Semanal',
};

/** Descreve o prazo contratual do serviço */
function describeTerm(item: ProposalSnapshotItem): string {
  if (item.term_type === 'indeterminate' || !item.term_type) return 'Indeterminado';
  if (item.term_quantity) {
    const unitLabels: Record<string, string> = {
      day: 'dia(s)',
      week: 'semana(s)',
      month: 'mês(es)',
      year: 'ano(s)',
    };
    return `${item.term_quantity} ${unitLabels[item.term_unit ?? 'month'] ?? item.term_unit}`;
  }
  return 'Indeterminado';
}

/** Descreve o desconto aplicado no item */
function describeDiscount(item: ProposalSnapshotItem): string {
  const percent = num(item.discount_percent);
  const fixed = num(item.discount_fixed);
  if (percent > 0) return `${percent.toLocaleString('pt-BR')}%`;
  if (fixed > 0) return `- ${money(fixed)}`;
  return '—';
}

/** Cabeçalho de seção (ícone colorido + título + contagem) */
function SectionHeader({
  icon: Icon,
  title,
  count,
  toneClass,
}: {
  icon: typeof Package;
  title: string;
  count: number;
  toneClass: string;
}) {
  return (
    <div className="flex items-center gap-2 mb-2.5">
      <div className={cn('h-7 w-7 rounded-lg flex items-center justify-center', toneClass)}>
        <Icon className="h-3.5 w-3.5" />
      </div>
      <h3 className="text-[11px] font-semibold uppercase tracking-wider text-foreground">
        {title} ({count})
      </h3>
    </div>
  );
}

/**
 * Tabelas de Produtos e Serviços do snapshot da proposta.
 */
export function ProposalItemsTables({ items, currency = 'BRL' }: ProposalItemsTablesProps) {
  const products = items.filter((i) => i.product_type !== 'service');
  const services = items.filter((i) => i.product_type === 'service');

  if (items.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border py-10 text-center">
        <Package className="h-5 w-5 mx-auto text-muted-foreground mb-2" />
        <p className="text-xs text-muted-foreground">Nenhum item nesta proposta.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ---------------------------- PRODUTOS ---------------------------- */}
      {products.length > 0 && (
        <section>
          <SectionHeader
            icon={Package}
            title="Produtos"
            count={products.length}
            toneClass="bg-amber-500/10 text-amber-600 dark:text-amber-400"
          />
          <div className="rounded-xl border border-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left min-w-[560px]">
                <thead className="bg-muted/50 border-b border-border">
                  <tr className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    <th className="px-4 py-2.5">Produto</th>
                    <th className="px-4 py-2.5 text-center">Un</th>
                    <th className="px-4 py-2.5 text-center">Qtd</th>
                    <th className="px-4 py-2.5 text-right">Preço</th>
                    <th className="px-4 py-2.5 text-right">Desconto</th>
                    <th className="px-4 py-2.5 text-right">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {products.map((item) => (
                    <tr key={item.id} className="hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3">
                        <span className="block font-medium text-foreground">{item.product_name}</span>
                        {item.product_sku && (
                          <span className="text-[10px] uppercase text-muted-foreground">
                            {item.product_sku}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center text-muted-foreground">
                        {item.unit_type ?? 'un'}
                      </td>
                      <td className="px-4 py-3 text-center text-muted-foreground">
                        {num(item.quantity).toLocaleString('pt-BR')}
                      </td>
                      <td className="px-4 py-3 text-right text-muted-foreground">
                        {money(item.unit_price, currency)}
                      </td>
                      <td className="px-4 py-3 text-right text-muted-foreground">
                        {describeDiscount(item)}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-foreground">
                        {money(item.total, currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {/* ----------------------- SERVIÇOS RECORRENTES ---------------------- */}
      {services.length > 0 && (
        <section>
          <SectionHeader
            icon={Wrench}
            title="Serviços recorrentes"
            count={services.length}
            toneClass="bg-blue-500/10 text-blue-600 dark:text-blue-400"
          />
          <div className="rounded-xl border border-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left min-w-[620px]">
                <thead className="bg-muted/50 border-b border-border">
                  <tr className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    <th className="px-4 py-2.5">Serviço</th>
                    <th className="px-4 py-2.5 text-center">Ciclo</th>
                    <th className="px-4 py-2.5 text-center">Prazo</th>
                    <th className="px-4 py-2.5 text-center">Qtd</th>
                    <th className="px-4 py-2.5 text-right">Mensalidade</th>
                    <th className="px-4 py-2.5 text-right">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {services.map((item) => (
                    <tr key={item.id} className="hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3">
                        <span className="block font-medium text-foreground">{item.product_name}</span>
                        {item.product_sku && (
                          <span className="text-[10px] uppercase text-muted-foreground">
                            {item.product_sku}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="px-2 py-0.5 rounded bg-muted text-[10px] font-semibold uppercase text-muted-foreground">
                          {RECURRENCE_LABELS[item.recurrence_interval ?? ''] ?? 'Recorrente'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center text-muted-foreground">
                        {describeTerm(item)}
                      </td>
                      <td className="px-4 py-3 text-center text-muted-foreground">
                        {num(item.quantity).toLocaleString('pt-BR')}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-blue-600 dark:text-blue-400">
                        {money(item.recurring_price ?? item.unit_price, currency)}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-foreground">
                        {money(item.total, currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
